<template>
  <v-menu
    v-model="exportAllMenu"
    :close-on-content-click="false"
    offset-y
  >
    <template #activator="{ on: menuOn, attrs: menuAttrs }">
      <v-tooltip
        v-bind="defaultVTooltipProps"
        open-on-hover
        top
      >
        <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
          <v-btn
            v-bind="{ ...menuAttrs, ...tooltipAttrs }"
            v-on="{ ...menuOn, ...tooltipOn }"
            :disabled="selectedStructures.length === 0"
            aria-label="Generate and export all reports to XLSX"
          >
            <v-icon>mdi-file-table-box-multiple-outline</v-icon>
          </v-btn>
        </template>

        <span>Generate hazard, safety and alerts reports and export into one XLSX</span>
      </v-tooltip>
    </template>

    <v-card min-width="200">
      <v-list>
        <v-list-item v-if="feature('hazard')">
          <v-list-item-action>
            <v-switch
              color="blue"
              v-model="exportAllHazard"
            />
          </v-list-item-action>
          <v-list-item-title>Hazard Report</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="feature('hazard')">
          <v-list-item-action>
            <v-switch
              color="blue"
              v-model="exportAllHazard2"
            />
          </v-list-item-action>
          <v-list-item-title>Hazard Report Split</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="feature('safety')">
          <v-list-item-action>
            <v-switch
              color="blue"
              v-model="exportAllSafety"
            />
          </v-list-item-action>
          <v-list-item-title>Safety Report</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="feature('alerts')">
          <v-list-item-action>
            <v-switch
              color="blue"
              v-model="exportAllAlerts"
            />
          </v-list-item-action>
          <v-list-item-title>Alerts Report</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="feature('webtest')">
          <v-list-item-action>
            <v-switch
              color="blue"
              v-model="exportPredictions"
            />
          </v-list-item-action>
          <v-list-item-title>WebTEST v1 Report</v-list-item-title>
        </v-list-item>
      </v-list>

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          @click="exportAllMenu = false"
          aria-label="Cancel"
        >
          Cancel
        </v-btn>
        <v-btn
          :disabled="
            !this.exportAllAlerts &&
            !this.exportAllSafety &&
            !this.exportAllHazard &&
            !this.exportAllHazard2 &&
            !this.exportPredictions
          "
          color="primary"
          text
          @click="
            exportAllMenu = false
            onExportAllCollated()
          "
          aria-label="Generate"
        >
          Generate
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import { mapGetters } from 'vuex'
import { download, getDatedFilename, SERVICES_URL } from '@/main'
import { showErrorToastWithTimeout } from '@/utils/toast'
import { defaultVTooltipProps } from '@/utils/defaultProps'
import { alertsOptionsPreference } from '@/components/alerts/alertsPreferences'

export default {
  props: {
    selectedStructures: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      exportAllMenu: false,
      exportAllHazard: true,
      exportAllHazard2: false,
      exportAllAlerts: true,
      exportAllSafety: true,
      exportPredictions: true,
      downloading: false,
      defaultVTooltipProps,
    }
  },
  watch: {
    exportAllHazard2: {
      handler() {
        this.exportAllHazard = !this.exportAllHazard2
      },
    },
    exportAllHazard: {
      handler() {
        this.exportAllHazard2 = !this.exportAllHazard
      },
    },
    downloading: {
      handler(newVal) {
        this.$emit('input', newVal)
      },
      deep: true,
    },
    value: {
      handler(newVal) {
        this.downloading = newVal
      },
    },
  },
  computed: {
    ...mapGetters(['feature']),
    alertsOptions() {
      return alertsOptionsPreference.value
    },
  },
  methods: {
    async onExportAllCollated() {
      this.downloading = true
      const url = `${SERVICES_URL}/collated_report`
      let data = {
        exportHazard: this.exportAllHazard,
        exportHazard2: this.exportAllHazard2,
        exportSafety: this.exportAllSafety,
        exportAlerts: this.exportAllAlerts,
        exportPrediction: this.exportPredictions,
      }
      if (data.exportHazard || data.exportHazard2) {
        data.hazardRequest = JSON.stringify({
          options: this.getHazardOptions,
          chemicals: this.selectedStructures.map((s) => ({
            chemical: s,
            properties: {
              similarity: s.similarity,
            },
          })),
        })
      }
      if (data.exportSafety) {
        data.chemicals = JSON.stringify(this.selectedStructures)
      }
      if (data.exportAlerts) {
        data.alertRequest = JSON.stringify({
          options: this.alertsOptions,
          chemicals: this.selectedStructures,
        })
      }
      if (data.exportPrediction) {
        data.predictionRequest = JSON.stringify({
          report: 'HTML',
          structures: this.selectedStructures.map((s) => s.cid || s.sid || s.casrn || s.smiles).filter((s) => !!s),
        })
      }
      await this.$axios
        .post(url, data, { responseType: 'blob' })
        .then((response) => {
          let filename = getDatedFilename('full', 'xlsx')
          download(filename, response.data, 'xlsx')
        })
        .catch((error) => {
          console.error('Error during export:', error)
          showErrorToastWithTimeout(error, 'Error during export')
        })
      this.downloading = false
    },
  },
}
</script>

<style scoped></style>
